import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Sparkles, FileText, Download, RefreshCw, Check } from 'lucide-react';
import { api, Account } from '../../lib/api';

interface EvidenceDetail {
  id: string;
  fileName: string;
  fileKey: string;
  fileSize: number;
  mimeType: string;
  ocrStatus: string;
  ocrResult: any;
  extractedDate: string | null;
  extractedAmount: string | null;
  extractedVendor: string | null;
  extractedInvoiceNumber: string | null;
  linkedJournalId: string | null;
  uploaderName: string;
  createdAt: string;
}

export function EvidenceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [journalDate, setJournalDate] = useState('');
  const [debitAccountId, setDebitAccountId] = useState('');
  const [creditAccountId, setCreditAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');

  const { data: evidence, isLoading } = useQuery({
    queryKey: ['evidence', id],
    queryFn: () => api.get<EvidenceDetail>(`/api/evidence/${id}`),
    enabled: !!id,
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<{ data: Account[] }>('/api/accounts'),
  });

  const ocrMutation = useMutation({
    mutationFn: () => api.post(`/api/evidence/${id}/ocr`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evidence', id] });
    },
  });

  const createJournalMutation = useMutation({
    mutationFn: async () => {
      const journalAmount = parseFloat(amount);
      const response = await api.post('/api/journals', {
        journalDate,
        journalType: 'normal',
        description: description || `${evidence?.extractedVendor || ''}からの請求`,
        lines: [
          {
            accountId: debitAccountId,
            debitAmount: journalAmount,
            creditAmount: 0,
            description: evidence?.extractedVendor || '',
          },
          {
            accountId: creditAccountId,
            debitAmount: 0,
            creditAmount: journalAmount,
            description: evidence?.extractedVendor || '',
          },
        ],
      });
      
      // 証憑と仕訳を紐付け
      if (response && (response as any).id) {
        await api.post(`/api/evidence/${id}/link-journal`, {
          journalId: (response as any).id,
        });
      }
      
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evidence', id] });
      navigate('/journals');
    },
  });

  // OCR結果から初期値を設定
  const applyOcrResult = () => {
    if (evidence) {
      if (evidence.extractedDate) {
        setJournalDate(evidence.extractedDate);
      }
      if (evidence.extractedAmount) {
        setAmount(evidence.extractedAmount);
      }
      if (evidence.extractedVendor) {
        setDescription(`${evidence.extractedVendor}からの請求`);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!evidence) {
    return (
      <div className="text-center py-12">
        <p style={{ color: 'var(--text-secondary)' }}>証憑が見つかりません</p>
      </div>
    );
  }

  const canRunOcr = evidence.ocrStatus === 'pending' || evidence.ocrStatus === 'failed';
  const ocrCompleted = evidence.ocrStatus === 'completed';

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/evidence')}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <ArrowLeft size={20} style={{ color: 'var(--text-primary)' }} />
        </button>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          証憑詳細
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 左側: 証憑情報 + OCR結果 */}
        <div className="space-y-6">
          {/* 証憑基本情報 */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              証憑情報
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>ファイル名</span>
                <span style={{ color: 'var(--text-primary)' }}>{evidence.fileName}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>ファイルサイズ</span>
                <span style={{ color: 'var(--text-primary)' }}>
                  {(evidence.fileSize / 1024).toFixed(1)} KB
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>アップロード者</span>
                <span style={{ color: 'var(--text-primary)' }}>{evidence.uploaderName}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>OCRステータス</span>
                <span className={`badge ${
                  evidence.ocrStatus === 'completed' ? 'badge-success' :
                  evidence.ocrStatus === 'processing' ? 'badge-info' :
                  evidence.ocrStatus === 'failed' ? 'badge-error' : 'badge-warning'
                }`}>
                  {evidence.ocrStatus === 'completed' ? '完了' :
                   evidence.ocrStatus === 'processing' ? '処理中' :
                   evidence.ocrStatus === 'failed' ? '失敗' : '未処理'}
                </span>
              </div>
              {evidence.linkedJournalId && (
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-secondary)' }}>紐付け仕訳</span>
                  <span className="badge badge-success">紐付け済み</span>
                </div>
              )}
            </div>
            
            <div className="flex gap-2 mt-4">
              <a
                href={`https://totaros-erp-app.valleyintest.workers.dev/api/evidence/${id}/download`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              >
                <Download size={16} />
                ダウンロード
              </a>
              {canRunOcr && (
                <button
                  onClick={() => ocrMutation.mutate()}
                  disabled={ocrMutation.isPending}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-white text-sm"
                  style={{ backgroundColor: 'var(--accent-color)' }}
                >
                  {ocrMutation.isPending ? (
                    <RefreshCw size={16} className="animate-spin" />
                  ) : (
                    <Sparkles size={16} />
                  )}
                  {evidence.ocrStatus === 'failed' ? 'OCR再実行' : 'OCR実行'}
                </button>
              )}
            </div>
          </div>

          {/* OCR結果 */}
          {ocrCompleted && evidence.ocrResult && (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                  AI-OCR 抽出結果
                </h2>
                <button
                  onClick={applyOcrResult}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm"
                  style={{ backgroundColor: 'var(--accent-color)', color: 'white' }}
                >
                  <Check size={14} />
                  仕訳に適用
                </button>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-secondary)' }}>取引日</span>
                  <span className="font-mono" style={{ color: 'var(--text-primary)' }}>
                    {evidence.extractedDate || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-secondary)' }}>金額</span>
                  <span className="font-mono font-bold" style={{ color: 'var(--text-primary)' }}>
                    {evidence.extractedAmount 
                      ? `¥${parseFloat(evidence.extractedAmount).toLocaleString()}` 
                      : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-secondary)' }}>取引先</span>
                  <span style={{ color: 'var(--text-primary)' }}>
                    {evidence.extractedVendor || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-secondary)' }}>インボイス番号</span>
                  <span className="font-mono" style={{ color: 'var(--text-primary)' }}>
                    {evidence.extractedInvoiceNumber || '-'}
                  </span>
                </div>
                {evidence.ocrResult.items && evidence.ocrResult.items.length > 0 && (
                  <div>
                    <span style={{ color: 'var(--text-secondary)' }}>品目</span>
                    <ul className="mt-1 ml-4 list-disc" style={{ color: 'var(--text-primary)' }}>
                      {evidence.ocrResult.items.map((item: any, i: number) => (
                        <li key={i}>
                          {typeof item === 'string' 
                            ? item 
                            : typeof item === 'object' && item !== null
                              ? (item.name || item.description || JSON.stringify(item))
                              : String(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {evidence.ocrResult.taxAmount && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-secondary)' }}>消費税額</span>
                    <span className="font-mono" style={{ color: 'var(--text-primary)' }}>
                      ¥{parseFloat(evidence.ocrResult.taxAmount).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 処理中メッセージ */}
          {evidence.ocrStatus === 'processing' && (
            <div className="card p-6 text-center">
              <RefreshCw size={32} className="mx-auto mb-4 animate-spin" style={{ color: 'var(--accent-color)' }} />
              <p style={{ color: 'var(--text-primary)' }}>OCR処理中...</p>
              <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                しばらくお待ちください
              </p>
            </div>
          )}

          {/* 失敗メッセージ */}
          {evidence.ocrStatus === 'failed' && (
            <div className="card p-6 border-l-4" style={{ borderLeftColor: 'var(--error)' }}>
              <p className="font-medium" style={{ color: 'var(--error)' }}>OCR処理に失敗しました</p>
              <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                「OCR再実行」ボタンで再度処理を行えます
              </p>
            </div>
          )}
        </div>

        {/* 右側: 仕訳作成フォーム */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={20} style={{ color: 'var(--accent-color)' }} />
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              仕訳作成
            </h2>
          </div>

          {evidence.linkedJournalId ? (
            <div className="text-center py-8">
              <Check size={48} className="mx-auto mb-4" style={{ color: 'var(--success)' }} />
              <p style={{ color: 'var(--text-primary)' }}>この証憑は既に仕訳と紐付けされています</p>
              <button
                onClick={() => navigate('/journals')}
                className="mt-4 px-4 py-2 rounded-lg text-sm"
                style={{ backgroundColor: 'var(--accent-color)', color: 'white' }}
              >
                仕訳一覧へ
              </button>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createJournalMutation.mutate();
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  取引日 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={journalDate}
                  onChange={(e) => setJournalDate(e.target.value)}
                  required
                  className="w-full px-3 py-2 rounded-lg border"
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    borderColor: 'var(--input-border)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  金額 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  min="0"
                  step="1"
                  className="w-full px-3 py-2 rounded-lg border font-mono"
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    borderColor: 'var(--input-border)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  借方科目 <span className="text-red-500">*</span>
                </label>
                <select
                  value={debitAccountId}
                  onChange={(e) => setDebitAccountId(e.target.value)}
                  required
                  className="w-full px-3 py-2 rounded-lg border"
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    borderColor: 'var(--input-border)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">選択してください</option>
                  {accounts?.data
                    .filter((a) => a.accountType === 'expense')
                    .map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.code} - {account.name}
                      </option>
                    ))}
                  <optgroup label="その他">
                    {accounts?.data
                      .filter((a) => a.accountType !== 'expense')
                      .map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.code} - {account.name}
                        </option>
                      ))}
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  貸方科目 <span className="text-red-500">*</span>
                </label>
                <select
                  value={creditAccountId}
                  onChange={(e) => setCreditAccountId(e.target.value)}
                  required
                  className="w-full px-3 py-2 rounded-lg border"
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    borderColor: 'var(--input-border)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">選択してください</option>
                  {accounts?.data
                    .filter((a) => a.accountType === 'asset' || a.accountType === 'liability')
                    .map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.code} - {account.name}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  摘要
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="取引の説明"
                  className="w-full px-3 py-2 rounded-lg border"
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    borderColor: 'var(--input-border)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={createJournalMutation.isPending || !journalDate || !amount || !debitAccountId || !creditAccountId}
                className="w-full py-3 rounded-lg text-white font-medium disabled:opacity-50"
                style={{ backgroundColor: 'var(--accent-color)' }}
              >
                {createJournalMutation.isPending ? '作成中...' : '仕訳を作成'}
              </button>

              {createJournalMutation.isError && (
                <p className="text-sm text-center" style={{ color: 'var(--error)' }}>
                  仕訳の作成に失敗しました
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

